import * as Sentry from '@sentry/react';

const STORAGE_KEY = 'gpm:page-lifecycle';
const HEARTBEAT_MS = 5000;

type LifecycleRecord = {
  route: string;
  startedAt: number;
  lastBeatAt: number;
  cleanExit: boolean;
  exitReason?: string;
  /** Page-supplied detail (COG layer count, marker count, …). */
  context: Record<string, number | string | boolean>;
};

let current: LifecycleRecord | null = null;
let heartbeat: number | undefined;

const read = (): LifecycleRecord | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LifecycleRecord) : null;
  } catch {
    return null;
  }
};

const write = (record: LifecycleRecord) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // sessionStorage can throw in private mode / when full — diagnostics are
    // never worth breaking the page for.
  }
};

const navigationType = (): string => {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type ?? 'unknown';
};

const reportUnexpectedReload = (previous: LifecycleRecord) => {
  const uptimeSec = Math.round((previous.lastBeatAt - previous.startedAt) / 1000);

  Sentry.captureMessage('Unexpected page reload (previous page never unloaded)', {
    level: 'error',
    tags: {
      diagnostic: 'unexpected-reload',
      lastRoute: previous.route,
      navigationType: navigationType(),
    },
    extra: {
      ...previous.context,
      lastRoute: previous.route,
      uptimeSec,
      // Time between the last heartbeat and this load — a large gap means the
      // tab sat frozen/backgrounded before dying, a small one means it died
      // while the user was on it.
      gapSec: Math.round((Date.now() - previous.lastBeatAt) / 1000),
      navigationType: navigationType(),
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    },
  });
};

/** Marks the current page as intentionally leaving, so it is not reported as a kill. */
export const markCleanExit = (reason: string) => {
  if (!current) return;
  current = { ...current, cleanExit: true, exitReason: reason, lastBeatAt: Date.now() };
  write(current);
};

/** Page-level detail attached to the next unexpected-reload report. */
export const setPageLifecycleContext = (patch: Record<string, number | string | boolean>) => {
  if (!current) return;
  current = { ...current, context: { ...current.context, ...patch }, lastBeatAt: Date.now() };
  write(current);
};

export const initPageLifecycleWatchdog = () => {
  const previous = read();

  if (previous && !previous.cleanExit) {
    reportUnexpectedReload(previous);
  }

  current = {
    route: window.location.pathname,
    startedAt: Date.now(),
    lastBeatAt: Date.now(),
    cleanExit: false,
    context: {},
  };
  write(current);

  heartbeat = window.setInterval(() => {
    if (!current || current.cleanExit) return;
    current = { ...current, route: window.location.pathname, lastBeatAt: Date.now() };
    write(current);
  }, HEARTBEAT_MS);

  // `pagehide` is the only unload event iOS Safari fires reliably (it skips
  // `beforeunload` and often `unload` when the tab goes away).
  window.addEventListener('pagehide', (event) => markCleanExit(event.persisted ? 'bfcache' : 'pagehide'));

  // A bfcache'd page can come back alive without a fresh load — re-arm the
  // watchdog, otherwise a kill after the user returns would go unreported.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !current) return;
    current = { ...current, cleanExit: false, exitReason: undefined, lastBeatAt: Date.now() };
    write(current);
  });

  return () => {
    window.clearInterval(heartbeat);
  };
};
